import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IThemeProvider, SciChartJSDarkv2Theme, SciChartJSLightTheme, SciChartJsNavyTheme } from 'scichart';

export type LitTheme = 'light' | 'dark' | 'custom';

export type ColorPreset = 'default' | 'solarized' | 'dracula' | 'nord' | 'gruvbox' | 'onedark' | 'github' | 'catppuccin' | 'tokyonight' | 'terminal';

export interface ThemeColors {
  bg: string;
  text: string;
  primary: string;
  card: string;
  code: string;
  userBubbleText?: string; // Text color for user chat bubbles, defaults to white
  // Optional refinements a white-label brand may pin; every palette derives them otherwise.
  surface?: string;        // content areas between bg and card
  accent?: string;         // secondary brand color
  link?: string;           // link color when the primary is a poor link color (e.g. yellow)
}

export const COLOR_PRESETS: Record<'light' | 'dark', Record<ColorPreset, ThemeColors>> = {
  light: {
    default:    { bg: '#ffffff', text: '#0a0a0a', primary: '#2aa198', card: '#f8f8f8', code: '#f4f4f4' },
    solarized:  { bg: '#fdf6e3', text: '#657b83', primary: '#2aa198', card: '#eee8d5', code: '#eee8d5' },
    dracula:    { bg: '#f8f8f2', text: '#282a36', primary: '#bd93f9', card: '#f0f0f0', code: '#e8e8e8' },
    nord:       { bg: '#eceff4', text: '#2e3440', primary: '#5e81ac', card: '#e5e9f0', code: '#d8dee9' },
    gruvbox:    { bg: '#fbf1c7', text: '#3c3836', primary: '#d65d0e', card: '#f2e5bc', code: '#ebdbb2' },
    terminal:   { bg: '#f0f0f0', text: '#1a1a1a', primary: '#00aa00', card: '#e8e8e8', code: '#1a1a1a' },
    onedark:    { bg: '#fafafa', text: '#383a42', primary: '#4078f2', card: '#f0f0f0', code: '#e5e5e6' },
    github:     { bg: '#ffffff', text: '#24292f', primary: '#0969da', card: '#f6f8fa', code: '#f6f8fa' },
    catppuccin: { bg: '#eff1f5', text: '#4c4f69', primary: '#8839ef', card: '#e6e9ef', code: '#dce0e8' },
    tokyonight: { bg: '#d5d6db', text: '#343b58', primary: '#34548a', card: '#c8c9ce', code: '#c0c1c6' }
  },
  dark: {
    default:    { bg: '#002b36', text: '#fdf6e3', primary: '#2aa198', card: '#073642', code: '#00121d' },
    solarized:  { bg: '#002b36', text: '#839496', primary: '#2aa198', card: '#073642', code: '#00121d' },
    dracula:    { bg: '#282a36', text: '#f8f8f2', primary: '#bd93f9', card: '#44475a', code: '#1e1f29' },
    nord:       { bg: '#2e3440', text: '#eceff4', primary: '#88c0d0', card: '#3b4252', code: '#242933' },
    gruvbox:    { bg: '#282828', text: '#ebdbb2', primary: '#fe8019', card: '#3c3836', code: '#1d2021' },
    terminal:   { bg: '#0a0a0a', text: '#33ff33', primary: '#33ff33', card: '#1a1a1a', code: '#0a0a0a' },
    onedark:    { bg: '#282c34', text: '#abb2bf', primary: '#61afef', card: '#21252b', code: '#1e2127' },
    github:     { bg: '#0d1117', text: '#c9d1d9', primary: '#58a6ff', card: '#161b22', code: '#0d1117' },
    catppuccin: { bg: '#1e1e2e', text: '#cdd6f4', primary: '#cba6f7', card: '#313244', code: '#181825' },
    tokyonight: { bg: '#1a1b26', text: '#c0caf5', primary: '#7aa2f7', card: '#24283b', code: '#16161e' }
  }
};

const SCI_CHART_THEMES = {
  navy: new SciChartJsNavyTheme(),
  light: new SciChartJSLightTheme(),
  dark: new SciChartJSDarkv2Theme()
};

export enum DefaultChat {
  LastChat = 'last',
  NewChat = 'new'
}
export type ChartTheme = keyof typeof SCI_CHART_THEMES | 'auto' | 'auto-inverse';
export type ChartTraffic = 'POLLING' | 'SOCKET';
export type MessageWidth = 'narrow' | 'medium' | 'wide' | 'full';
export type WindowManagerType = 'gridster' | 'dockview';
export type MenubarPosition = 'top' | 'left' | 'right' | 'bottom';
@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private _desktopEnvironment: boolean;
  private _desktopEnvironment$ = new Subject<boolean>();
  public desktopEnvironment$ = this._desktopEnvironment$.asObservable();

  /** Check if this deployment is locked to full-screen mode */
  private get _isFullScreenOnly(): boolean {
    return window['env']?.whiteLabel?.fullScreenOnly === true;
  }

  /** Check if this deployment forces a specific layout engine */
  private get _forcedLayout(): 'dockview' | 'gridster' | undefined {
    return window['env']?.whiteLabel?.forceLayout;
  }

  public get desktopEnvironment(): boolean {
    return this._desktopEnvironment;
  }
  public set desktopEnvironment(v: boolean) {
    // Ignore attempts to switch to desktop mode if locked to full-screen
    if (this._isFullScreenOnly) return;
    // Ignore attempts to leave desktop mode if a layout is forced
    if (this._forcedLayout && !v) return;
    if (v === null || v === undefined) return;
    this._desktopEnvironment = v;
    localStorage.setItem('lit-desktop-environment', v ? 'true' : 'false');
    this._desktopEnvironment$.next(v);
  }

  private _theme: LitTheme;
  private _theme$ = new Subject<LitTheme>();
  public theme$ = this._theme$.asObservable();
  public get theme(): LitTheme {
    return this._theme;
  }
  public set theme(v: LitTheme) {
    if (v === null || v === undefined) return;
    document.body.parentElement.classList.replace(this._theme, v);
    this._theme = v;
    localStorage.setItem('lit-theme', v);
    this._theme$.next(v);
    // Reapply color preset for new theme mode
    this._applyThemeColors();
  }

  private _chartTheme: ChartTheme;
  private _chartTheme$ = new Subject<ChartTheme>();
  public chartTheme$ = this._chartTheme$.asObservable();
  public get chartTheme(): ChartTheme {
    return this._chartTheme;
  }
  public set chartTheme(v: ChartTheme) {
    if (v === null || v === undefined) return;
    this._chartTheme = v;
    localStorage.setItem('chart-theme', v);
    this._chartTheme$.next(v);
  }

  private _appBackground: string;
  private _appBackground$ = new Subject<string>();
  public appBackground$ = this._appBackground$.asObservable();
  public get appBackground(): string {
    return this._appBackground;
  }
  public set appBackground(v: string) {
    if (v === null || v === undefined) return;
    this._appBackground = v;
    document.body.style.cssText = `--bg-${this.backgroundType}: ${v}`;
    localStorage.setItem('app-background', v);
    this._appBackground$.next(v);
  }

  private _backgroundFit: string;
  private _backgroundFit$ = new Subject<string>();
  public backgroundFit$ = this._backgroundFit$.asObservable();
  public get backgroundFit(): string {
    return this._backgroundFit;
  }
  public set backgroundFit(v: string) {
    if (v === null || v === undefined) return;
    document.body.classList.replace(`bg-${this._backgroundFit}`, `bg-${v}`);
    this._backgroundFit = v;
    localStorage.setItem('background-fit', v);
    this._backgroundFit$.next(v);
  }

  private _menubarPosition: MenubarPosition;
  private _menubarPosition$ = new Subject<MenubarPosition>();
  public menubarPosition$ = this._menubarPosition$.asObservable();
  public get menubarPosition(): MenubarPosition {
    return this._menubarPosition;
  }
  public get isMenubarPositionLocked(): boolean {
    return !!window['env']?.whiteLabel?.menubarPosition;
  }

  public set menubarPosition(v: MenubarPosition) {
    if (v === null || v === undefined) return;
    // If white-label has locked the menubar position, ignore changes
    const locked = window['env']?.whiteLabel?.menubarPosition;
    if (locked) return;
    this._menubarPosition = v;
    localStorage.setItem('menubar-position', v);
    this._menubarPosition$.next(v);
  }

  private _colorPreset: ColorPreset | null;
  private _colorPreset$ = new Subject<ColorPreset | null>();
  public colorPreset$ = this._colorPreset$.asObservable();
  public get colorPreset(): ColorPreset | null {
    return this._colorPreset;
  }
  public set colorPreset(v: ColorPreset | null) {
    this._colorPreset = v;
    localStorage.setItem('color-preset', v ?? '');
    // When selecting a preset, clear any custom colors
    if (v) {
      this._customColors = null;
      localStorage.removeItem('custom-colors');
    }
    this._applyThemeColors();
    this._colorPreset$.next(v);
  }

  private _customColors: ThemeColors | null;
  private _customColors$ = new Subject<ThemeColors | null>();
  public customColors$ = this._customColors$.asObservable();
  public get customColors(): ThemeColors | null {
    return this._customColors;
  }
  public set customColors(v: ThemeColors | null) {
    this._customColors = v;
    if (v) {
      localStorage.setItem('custom-colors', JSON.stringify(v));
      // When setting custom colors, clear any preset
      this._colorPreset = null;
      localStorage.setItem('color-preset', '');
    } else {
      localStorage.removeItem('custom-colors');
    }
    this._applyThemeColors();
    this._customColors$.next(v);
  }

  public setCustomColor(key: keyof ThemeColors, value: string): void {
    const current = this._customColors || this._getActiveColors() || this._getDefaultColors();
    this.customColors = { ...current, [key]: value };
  }

  private _getDefaultColors(): ThemeColors {
    const themeMode = this._theme === 'dark' ? 'dark' : 'light';
    return COLOR_PRESETS[themeMode].default;
  }

  public getActiveColors(): ThemeColors {
    return this._getActiveColors() || this._getDefaultColors();
  }

  private _getActiveColors(): ThemeColors | null {
    if (this._customColors) {
      return this._customColors;
    }
    if (this._colorPreset) {
      const themeMode = this._theme === 'dark' ? 'dark' : 'light';
      return COLOR_PRESETS[themeMode][this._colorPreset];
    }
    return null;
  }

  /** Paint one palette onto :root — the five core colors plus every derived token the
   *  stylesheets (and a white-label override.css) read, so a preset, custom colors and the
   *  brand palette all leave the UI fully specified. */
  private _paintPalette(root: HTMLElement, colors: ThemeColors, presetName: string): void {
    root.style.setProperty('--preset-bg', colors.bg);
    root.style.setProperty('--preset-text', colors.text);
    root.style.setProperty('--preset-primary', colors.primary);
    root.style.setProperty('--preset-card', colors.card);
    root.style.setProperty('--preset-code', colors.code);
    root.style.setProperty('--preset-text-muted', this.blendColors(colors.text, colors.bg, 0.4));
    root.style.setProperty('--preset-surface', colors.surface || this.blendColors(colors.card, colors.bg, 0.5));
    root.style.setProperty('--preset-accent', colors.accent || colors.primary);
    root.style.setProperty('--preset-link', colors.link || colors.primary);
    root.style.setProperty('--preset-border', this.withAlpha(colors.text, 0.08));
    root.style.setProperty('--preset-border-light', this.withAlpha(colors.text, 0.14));
    root.style.setProperty('--preset-hover', this.withAlpha(colors.primary, 0.10));
    root.style.setProperty('--preset-user-bubble', colors.primary);
    root.style.setProperty('--preset-user-bubble-text', colors.userBubbleText || '#ffffff');
    root.style.setProperty('--preset-ai-bubble', colors.card);
    root.setAttribute('data-color-preset', presetName);
  }

  /** The white-label brand palette for the current theme mode, if the deployment ships one. */
  public brandColors(): ThemeColors | null {
    const brandColors = (window['env']?.whiteLabel?.brandColors) as Record<string, ThemeColors> | undefined;
    if (!brandColors) return null;
    const themeMode = this._theme === 'dark' ? 'dark' : 'light';
    return brandColors[themeMode] || null;
  }
  public get brandName(): string | null {
    const wl = window['env']?.whiteLabel;
    return wl?.brandColors ? (wl.tabTitle || wl.name || 'Brand') : null;
  }

  private _applyThemeColors(): void {
    const root = document.documentElement;
    const colors = this._getActiveColors();

    if (!colors) {
      // No user choice: the white-label brand palette is the default when the deployment has one.
      const brand = this.brandColors();
      if (brand) {
        this._paintPalette(root, brand, 'brand');
        return;
      }
      // Clear inline colors — fall back to CSS stylesheet defaults
      for (const k of ['bg', 'text', 'text-muted', 'primary', 'card', 'code', 'surface', 'accent', 'link',
                       'border', 'border-light', 'hover', 'user-bubble', 'user-bubble-text', 'ai-bubble']) {
        root.style.removeProperty(`--preset-${k}`);
      }
      root.removeAttribute('data-color-preset');
      return;
    }

    this._paintPalette(root, colors, this._colorPreset || 'custom');
  }

  /** rgba() of a hex color (3/6 digits) or a passthrough for anything else. */
  private withAlpha(color: string, alpha: number): string {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((color || '').trim());
    if (!m) return color;
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private _fontSize: number;
  private _fontSize$ = new Subject<number>();
  public fontSize$ = this._fontSize$.asObservable();
  public get fontSize(): number {
    return this._fontSize;
  }
  public set fontSize(v: number) {
    if (v === null || v === undefined) return;
    this._fontSize = v;
    localStorage.setItem('font-size', v.toString());
    document.documentElement.style.setProperty('--user-font-size', `${v}px`);
    this._fontSize$.next(v);
  }

  private _toolCallsExpanded: boolean;
  public get toolCallsExpanded(): boolean {
    return this._toolCallsExpanded;
  }
  public set toolCallsExpanded(v: boolean) {
    this._toolCallsExpanded = v;
    localStorage.setItem('toolCallsExpanded', v ? 'true' : 'false');
  }

  private _uiDensity: 'compact' | 'default' | 'comfortable';
  private _uiDensity$ = new Subject<'compact' | 'default' | 'comfortable'>();
  public uiDensity$ = this._uiDensity$.asObservable();
  public get uiDensity(): 'compact' | 'default' | 'comfortable' {
    return this._uiDensity;
  }
  public set uiDensity(v: 'compact' | 'default' | 'comfortable') {
    if (v === null || v === undefined) return;
    this._uiDensity = v;
    localStorage.setItem('ui-density', v);
    document.documentElement.setAttribute('data-density', v);
    this._uiDensity$.next(v);
  }

  private _pollingRate: number;
  private _pollingRate$ = new Subject<number>();
  public pollingRate$ = this._pollingRate$.asObservable();
  public get pollingRate(): number {
    return this._pollingRate;
  }
  public set pollingRate(v: number) {
    if (v === null || v === undefined) return;
    this._pollingRate = v;
    localStorage.setItem('polling-rate', v.toString());
    this._pollingRate$.next(v);
  }

  private _chartTraffic: ChartTraffic;
  private _chartTraffic$ = new Subject<ChartTraffic>();
  public chartTraffic$ = this._chartTraffic$.asObservable();
  public get chartTraffic(): ChartTraffic {
    this._chartTraffic = localStorage.getItem('chart-traffic') as ChartTraffic;
    return this._chartTraffic ? this._chartTraffic : 'POLLING';
  }
  public set chartTraffic(v: ChartTraffic) {
    if (v === null || v === undefined) return;
    this._chartTraffic = v;
    localStorage.setItem('chart-traffic', v.toString());
    this._chartTraffic$.next(v);
  }

  private _defaultChat: DefaultChat;
  private _defaultChat$ = new Subject<DefaultChat>();
  public defaultChat$ = this._defaultChat$.asObservable();
  public get defaultChat(): DefaultChat {
    return this._defaultChat;
  }
  public set defaultChat(v: DefaultChat) {
    if (v === null || v === undefined) return;
    this._defaultChat = v;
    localStorage.setItem('default-chat', v);
    this._defaultChat$.next(v);
  }

  private _toolbarOpacity: number;
  private _toolbarOpacity$ = new Subject<number>();
  public toolbarOpacity$ = this._toolbarOpacity$.asObservable();
  public get toolbarOpacity(): number {
    return this._toolbarOpacity;
  }
  public set toolbarOpacity(v: number) {
    if (v === null || v === undefined) return;
    this._toolbarOpacity = v;
    localStorage.setItem('toolbar-opacity', v.toString());
    document.documentElement.style.setProperty('--toolbar-opacity', v.toString());
    this._toolbarOpacity$.next(v);
  }

  private _widgetOpacity: number;
  private _widgetOpacity$ = new Subject<number>();
  public widgetOpacity$ = this._widgetOpacity$.asObservable();
  public get widgetOpacity(): number {
    return this._widgetOpacity;
  }
  public set widgetOpacity(v: number) {
    if (v === null || v === undefined) return;
    this._widgetOpacity = v;
    localStorage.setItem('widget-opacity', v.toString());
    document.documentElement.style.setProperty('--widget-opacity', v.toString());
    this._widgetOpacity$.next(v);
  }

  private _messageWidth: MessageWidth;
  private _messageWidth$ = new Subject<MessageWidth>();
  public messageWidth$ = this._messageWidth$.asObservable();
  public get messageWidth(): MessageWidth {
    return this._messageWidth;
  }
  public set messageWidth(v: MessageWidth) {
    if (v === null || v === undefined) return;
    this._messageWidth = v;
    localStorage.setItem('message-width', v);
    document.documentElement.setAttribute('data-message-width', v);
    this._messageWidth$.next(v);
  }

  private _windowManagerType: WindowManagerType;
  private _windowManagerType$ = new Subject<WindowManagerType>();
  public windowManagerType$ = this._windowManagerType$.asObservable();
  public get windowManagerType(): WindowManagerType {
    return this._windowManagerType;
  }
  public set windowManagerType(v: WindowManagerType) {
    if (v === null || v === undefined) return;
    // Ignore attempts to change layout engine if forced by white-label
    if (this._forcedLayout && v !== this._forcedLayout) return;
    this._windowManagerType = v;
    localStorage.setItem('window-manager-type', v);
    this._windowManagerType$.next(v);
  }

  // Labs: Message Reactions
  private _labsReactions: boolean;
  private _labsReactions$ = new Subject<boolean>();
  public labsReactions$ = this._labsReactions$.asObservable();
  public get labsReactions(): boolean {
    return this._labsReactions;
  }
  public set labsReactions(v: boolean) {
    if (v === null || v === undefined) return;
    this._labsReactions = v;
    localStorage.setItem('labs-reactions', v ? 'true' : 'false');
    this._labsReactions$.next(v);
  }

  // Labs: Ambient Mode
  private _labsAmbientMode: boolean;
  private _labsAmbientMode$ = new Subject<boolean>();
  public labsAmbientMode$ = this._labsAmbientMode$.asObservable();
  public get labsAmbientMode(): boolean {
    return this._labsAmbientMode;
  }
  public set labsAmbientMode(v: boolean) {
    if (v === null || v === undefined) return;
    this._labsAmbientMode = v;
    localStorage.setItem('labs-ambient-mode', v ? 'true' : 'false');
    this._labsAmbientMode$.next(v);
  }

  // Audio: selected microphone device ID
  private _microphoneDeviceId: string;
  private _microphoneDeviceId$ = new Subject<string>();
  public microphoneDeviceId$ = this._microphoneDeviceId$.asObservable();
  public get microphoneDeviceId(): string {
    return this._microphoneDeviceId;
  }
  public set microphoneDeviceId(v: string) {
    this._microphoneDeviceId = v ?? '';
    localStorage.setItem('microphone-device-id', this._microphoneDeviceId);
    this._microphoneDeviceId$.next(this._microphoneDeviceId);
  }

  // Labs: Knowledge Graph
  private _labsKnowledgeGraph: boolean;
  private _labsKnowledgeGraph$ = new Subject<boolean>();
  public labsKnowledgeGraph$ = this._labsKnowledgeGraph$.asObservable();
  public get labsKnowledgeGraph(): boolean {
    return this._labsKnowledgeGraph;
  }
  public set labsKnowledgeGraph(v: boolean) {
    if (v === null || v === undefined) return;
    this._labsKnowledgeGraph = v;
    localStorage.setItem('labs-knowledge-graph', v ? 'true' : 'false');
    this._labsKnowledgeGraph$.next(v);
  }

  // Labs: Work Sessions
  private _labsWorkSessions: boolean;
  private _labsWorkSessions$ = new Subject<boolean>();
  public labsWorkSessions$ = this._labsWorkSessions$.asObservable();
  public get labsWorkSessions(): boolean {
    return this._labsWorkSessions;
  }
  public set labsWorkSessions(v: boolean) {
    if (v === null || v === undefined) return;
    this._labsWorkSessions = v;
    localStorage.setItem('labs-work-sessions', v ? 'true' : 'false');
    this._labsWorkSessions$.next(v);
  }

  public get hasCustomTheme(): 'light' | 'dark' | undefined {
    return window.env.prebuild.hasCustomTheme;
  }

  public get backgroundType(): 'image' | 'color' {
    return this._appBackground.startsWith('#') ? 'color' : 'image';
  }

  private _loadDesktopEnvironment(): boolean {
    // If locked to full-screen mode, always return false (chat mode)
    if (this._isFullScreenOnly) return false;

    // If a layout engine is forced, always return true (desktop/grid mode)
    if (this._forcedLayout) return true;

    const stored = localStorage.getItem('lit-desktop-environment');

    // If user has explicitly set a preference, use it
    if (stored === 'true') return true;
    if (stored === 'false') return false;

    // No preference stored - detect device type
    // Mobile devices default to full-screen (chat) mode, desktop defaults to windowed (grid) mode
    const isMobile = this._isMobileDevice();
    return !isMobile; // Desktop = true (grid mode), Mobile = false (chat mode)
  }

  private _isMobileDevice(): boolean {
    // Check for mobile user agent
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

    // Also check for touch-only device with small screen
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;

    return mobileRegex.test(userAgent.toLowerCase()) || (isTouchDevice && isSmallScreen);
  }

  private _loadTheme(): LitTheme {
    const stored = localStorage.getItem('lit-theme');

    if (stored === 'custom' && this.hasCustomTheme) return 'custom';
    if (stored === 'light') return 'light';
    if (stored === 'dark') return 'dark';

    // Check for white-label default when no user preference stored
    const whiteLabel = window['env']?.whiteLabel;
    if (whiteLabel?.defaultTheme === 'light') return 'light';
    if (whiteLabel?.defaultTheme === 'dark') return 'dark';

    if (Object.getOwnPropertyNames(window).indexOf('matchMedia') === -1) return 'light';
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';

    return 'light';
  }

  private _loadChartTheme(): ChartTheme {
    const stored = localStorage.getItem('chart-theme');

    if (stored in SCI_CHART_THEMES) return stored as ChartTheme;
    if (stored === 'auto-inverse') return 'auto-inverse';

    return 'auto';
  }

  private _loadAppBackground(): string {
    const stored = localStorage.getItem('app-background');
    if (stored) return stored;

    // Check for white-label default background
    const whiteLabel = window['env']?.whiteLabel;
    if (whiteLabel?.defaultBackground) {
      return whiteLabel.defaultBackground;
    }

    return '#1a1a2e';
  }

  private _loadBackgroundFit(): string {
    switch (localStorage.getItem('background-fit') ?? '') {
      case 'fill':
        return 'fill';
      case 'tile':
        return 'tile';
      case 'center':
        return 'center';
      default:
        return 'fill';
    }
  }

  private _isSmallViewport(): boolean {
    return window.innerWidth < 768;
  }

  private _loadMenubarPosition(): MenubarPosition {
    // White-label can force a specific position
    const locked = window['env']?.whiteLabel?.menubarPosition;
    if (locked && ['top', 'left', 'right', 'bottom'].includes(locked)) {
      return locked as MenubarPosition;
    }

    const stored = localStorage.getItem('menubar-position');
    if (stored && ['top', 'left', 'right', 'bottom'].includes(stored)) {
      return stored as MenubarPosition;
    }
    // Small viewports default to top (vertical menubar wastes horizontal space)
    return this._isSmallViewport() ? 'top' : 'left';
  }

  private _loadColorPreset(): ColorPreset | null {
    const stored = localStorage.getItem('color-preset');
    const validPresets: ColorPreset[] = ['default', 'solarized', 'dracula', 'nord', 'gruvbox', 'onedark', 'github', 'catppuccin', 'tokyonight', 'terminal'];
    if (stored && validPresets.includes(stored as ColorPreset)) {
      return stored as ColorPreset;
    }

    // Check for white-label settings when no user preference stored
    const whiteLabel = window['env']?.whiteLabel;
    // If brand colors are configured, skip preset fallback — brandColors handled in _applyThemeColors
    if (whiteLabel?.brandColors) {
      return null;
    }
    if (whiteLabel?.defaultColorPreset && validPresets.includes(whiteLabel.defaultColorPreset as ColorPreset)) {
      return whiteLabel.defaultColorPreset as ColorPreset;
    }

    return 'onedark';
  }

  private _loadCustomColors(): ThemeColors | null {
    const stored = localStorage.getItem('custom-colors');
    if (stored) {
      try {
        const colors = JSON.parse(stored);
        if (colors.bg && colors.text && colors.primary && colors.card && colors.code) {
          return colors as ThemeColors;
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
    return null;
  }

  /**
   * Blend two hex colors together.
   * @param color1 First hex color (e.g., '#ffffff')
   * @param color2 Second hex color (e.g., '#000000')
   * @param ratio Blend ratio (0 = all color1, 1 = all color2)
   * @returns Blended hex color
   */
  private blendColors(color1: string, color2: string, ratio: number): string {
    const hex = (c: string) => parseInt(c.replace('#', ''), 16);
    const r1 = (hex(color1) >> 16) & 255;
    const g1 = (hex(color1) >> 8) & 255;
    const b1 = hex(color1) & 255;
    const r2 = (hex(color2) >> 16) & 255;
    const g2 = (hex(color2) >> 8) & 255;
    const b2 = hex(color2) & 255;
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  private _loadFontSize(): number {
    const stored = localStorage.getItem('font-size');
    if (stored) {
      const size = parseInt(stored, 10);
      if (size >= 10 && size <= 24) return size;
    }
    return 14; // default
  }

  private _loadToolCallsExpanded(): boolean {
    return localStorage.getItem('toolCallsExpanded') === 'true';
  }

  private _loadUiDensity(): 'compact' | 'default' | 'comfortable' {
    const stored = localStorage.getItem('ui-density');
    if (stored === 'compact' || stored === 'default' || stored === 'comfortable') {
      return stored;
    }
    return 'default';
  }

  private _loadPollingRate(): number {
    const stored = localStorage.getItem('polling-rate');

    if (['30000', '60000', '180000', '300000'].includes(stored)) return parseInt(stored);

    return 60_000;
  }

  private _loadDefaultChat(): DefaultChat {
    switch (localStorage.getItem('default-chat') ?? '') {
      case DefaultChat.LastChat:
        return DefaultChat.LastChat;
      case DefaultChat.NewChat:
        return DefaultChat.NewChat;
      default:
        return DefaultChat.LastChat;
    }
  }

  private _loadToolbarOpacity(): number {
    const stored = localStorage.getItem('toolbar-opacity');
    if (stored) {
      const opacity = parseFloat(stored);
      if (opacity >= 0 && opacity <= 1) return opacity;
    }
    return 1; // default fully opaque
  }

  private _loadWidgetOpacity(): number {
    const stored = localStorage.getItem('widget-opacity');
    if (stored) {
      const opacity = parseFloat(stored);
      if (opacity >= 0 && opacity <= 1) return opacity;
    }
    return 1; // default fully opaque
  }

  private _loadMessageWidth(): MessageWidth {
    const stored = localStorage.getItem('message-width');
    if (stored === 'narrow' || stored === 'medium' || stored === 'wide' || stored === 'full') {
      return stored;
    }
    return 'full'; // default - better for widget rendering
  }

  private _loadWindowManagerType(): WindowManagerType {
    // If forced by white-label, always use that layout engine
    if (this._forcedLayout) return this._forcedLayout;
    const stored = localStorage.getItem('window-manager-type');
    if (stored === 'dockview' || stored === 'gridster') return stored;
    return 'dockview';
  }

  private _loadLabsReactions(): boolean {
    return localStorage.getItem('labs-reactions') === 'true';
  }

  private _loadLabsAmbientMode(): boolean {
    const stored = localStorage.getItem('labs-ambient-mode');
    if (stored === 'false') return false;
    if (stored === 'true') return true;
    return true;
  }

  private _loadLabsKnowledgeGraph(): boolean {
    const stored = localStorage.getItem('labs-knowledge-graph');
    if (stored === 'false') return false;
    if (stored === 'true') return true;
    return true;
  }

  private _loadLabsWorkSessions(): boolean {
    const stored = localStorage.getItem('labs-work-sessions');
    if (stored === 'false') return false;
    if (stored === 'true') return true;
    return true; // default on
  }

  // Background image slots (4 total: 2 defaults + 2 custom)
  public static readonly DEFAULT_BACKGROUNDS = [
    'url(/assets/yosemite-2.webp)',
    'url(/assets/manhattan.webp)'
  ];
  public static readonly SLOT_COUNT = 4;

  private _backgroundSlots: (string | null)[];
  private _backgroundSlots$ = new Subject<(string | null)[]>();
  public backgroundSlots$ = this._backgroundSlots$.asObservable();
  public get backgroundSlots(): (string | null)[] {
    return this._backgroundSlots;
  }

  public setBackgroundSlot(index: number, value: string | null): void {
    if (index < 0 || index >= SettingsService.SLOT_COUNT) return;
    this._backgroundSlots[index] = value;
    // Only store URLs, not base64 data (those are stored on the server)
    localStorage.setItem('background-slots', JSON.stringify(this._backgroundSlots));
    this._backgroundSlots$.next([...this._backgroundSlots]);
  }

  private _loadBackgroundSlots(): (string | null)[] {
    const stored = localStorage.getItem('background-slots');
    if (stored) {
      try {
        const slots = JSON.parse(stored);
        if (Array.isArray(slots) && slots.length === SettingsService.SLOT_COUNT) {
          return slots;
        }
      } catch {
        // Invalid JSON, use defaults
      }
    }
    // Initialize with defaults: 2 pre-filled, 2 empty
    return [
      SettingsService.DEFAULT_BACKGROUNDS[0],
      SettingsService.DEFAULT_BACKGROUNDS[1],
      null,
      null
    ];
  }

  constructor() {
    window.$settings = this;

    this._desktopEnvironment = this._loadDesktopEnvironment();
    this._theme = this._loadTheme();
    this._chartTheme = this._loadChartTheme();
    this._appBackground = this._loadAppBackground();
    this._backgroundFit = this._loadBackgroundFit();
    this._menubarPosition = this._loadMenubarPosition();
    this._pollingRate = this._loadPollingRate();
    this._defaultChat = this._loadDefaultChat();
    this._colorPreset = this._loadColorPreset();
    this._customColors = this._loadCustomColors();
    this._fontSize = this._loadFontSize();
    this._toolCallsExpanded = this._loadToolCallsExpanded();
    this._uiDensity = this._loadUiDensity();
    this._toolbarOpacity = this._loadToolbarOpacity();
    this._widgetOpacity = this._loadWidgetOpacity();
    this._messageWidth = this._loadMessageWidth();
    this._backgroundSlots = this._loadBackgroundSlots();
    this._windowManagerType = this._loadWindowManagerType();
    this._labsReactions = this._loadLabsReactions();
    this._microphoneDeviceId = localStorage.getItem('microphone-device-id') || '';
    this._labsAmbientMode = this._loadLabsAmbientMode();
    this._labsKnowledgeGraph = this._loadLabsKnowledgeGraph();
    this._labsWorkSessions = this._loadLabsWorkSessions();

    // Apply theme colors (preset or custom) on startup
    this._applyThemeColors();

    // Apply font size on startup
    document.documentElement.style.setProperty('--user-font-size', `${this._fontSize}px`);

    // Apply UI density on startup
    document.documentElement.setAttribute('data-density', this._uiDensity);

    // Apply opacity settings on startup
    document.documentElement.style.setProperty('--toolbar-opacity', this._toolbarOpacity.toString());
    document.documentElement.style.setProperty('--widget-opacity', this._widgetOpacity.toString());

    // Apply message width on startup
    document.documentElement.setAttribute('data-message-width', this._messageWidth);
  }

  public getChartThemeName(): ChartTheme {
    if (this._chartTheme in SCI_CHART_THEMES) return this._chartTheme;

    if (this._chartTheme === 'auto') return this._theme === 'light' ? 'navy' : 'light';
    if (this._chartTheme === 'auto-inverse') return this._theme === 'light' ? 'light' : 'navy';

    return 'navy';
  }

  public getChartThemeProvider(): IThemeProvider {
    return SCI_CHART_THEMES[this.getChartThemeName()];
  }
}
